import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Alert from '@material-ui/lab/Alert';
import { useNavigate } from 'react-router-dom';
import { firestore } from '../../firebase/config';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
  },
  table: {
    minWidth: 650,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  title: {
    flex: '1 1 100%',
    marginBottom: theme.spacing(2),
  },
  button: {
    margin: theme.spacing(1),
  },
  headerActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchField: {
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: '300px',
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3),
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  formControl: {
    minWidth: 120,
  },
}));

const CouponsListPage = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [couponToDelete, setCouponToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [filters, setFilters] = useState({
    status: 'all',
    type: 'all'
  });

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      // Simulation de récupération des données depuis Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      const couponsCollection = collection(firestore, 'coupons');
      const couponsSnapshot = await getDocs(couponsCollection);
      
      // Si la collection n'existe pas encore ou est vide, utiliser des données de démonstration
      if (couponsSnapshot.empty) {
        const demoCoupons = [
          { 
            id: 'WELCOME10', 
            code: 'WELCOME10',
            description: 'Coupon de bienvenue pour les nouveaux utilisateurs',
            discountType: 'percentage',
            discountValue: 10,
            minOrderValue: 0,
            maxDiscountAmount: 50,
            usageLimit: 1,
            usageCount: 45,
            startDate: '2025-01-01T00:00:00',
            endDate: '2025-12-31T23:59:59',
            status: 'active',
            createdAt: '2025-01-01T10:00:00',
            updatedAt: '2025-01-01T10:00:00'
          },
          { 
            id: 'SUMMER25', 
            code: 'SUMMER25',
            description: 'Réduction pour la saison estivale',
            discountType: 'percentage',
            discountValue: 25,
            minOrderValue: 100,
            maxDiscountAmount: 200,
            usageLimit: 0,
            usageCount: 120,
            startDate: '2025-06-01T00:00:00',
            endDate: '2025-08-31T23:59:59',
            status: 'active',
            createdAt: '2025-05-15T14:30:00',
            updatedAt: '2025-05-15T14:30:00'
          },
          { 
            id: 'FLASH50', 
            code: 'FLASH50',
            description: 'Vente flash - 50€ de réduction',
            discountType: 'fixed',
            discountValue: 50,
            minOrderValue: 200,
            maxDiscountAmount: 50,
            usageLimit: 100,
            usageCount: 98,
            startDate: '2025-04-01T00:00:00',
            endDate: '2025-04-03T23:59:59',
            status: 'expired',
            createdAt: '2025-03-25T09:15:00',
            updatedAt: '2025-03-25T09:15:00'
          },
          { 
            id: 'WEEKEND15', 
            code: 'WEEKEND15',
            description: 'Réduction pour les voyages du weekend',
            discountType: 'percentage',
            discountValue: 15,
            minOrderValue: 50,
            maxDiscountAmount: 100,
            usageLimit: 0,
            usageCount: 67,
            startDate: '2025-03-01T00:00:00',
            endDate: '2025-12-31T23:59:59',
            status: 'active',
            createdAt: '2025-02-20T11:45:00',
            updatedAt: '2025-02-20T11:45:00'
          },
          { 
            id: 'FAMILY20', 
            code: 'FAMILY20',
            description: 'Réduction pour les réservations familiales (3+ personnes)',
            discountType: 'percentage',
            discountValue: 20,
            minOrderValue: 150,
            maxDiscountAmount: 0,
            usageLimit: 0,
            usageCount: 34,
            startDate: '2025-01-01T00:00:00',
            endDate: '2025-12-31T23:59:59',
            status: 'active',
            createdAt: '2025-01-10T16:20:00',
            updatedAt: '2025-01-10T16:20:00'
          },
        ];
        setCoupons(demoCoupons);
      } else {
        const fetchedCoupons = couponsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setCoupons(fetchedCoupons);
      }
    } catch (error) {
      console.error('Error fetching coupons:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la récupération des coupons',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = () => {
    navigate('/admin/coupons/create');
  };

  const handleViewCoupon = (couponId) => {
    navigate(`/admin/coupons/${couponId}`);
  };

  const handleEditCoupon = (couponId) => {
    navigate(`/admin/coupons/${couponId}/edit`);
  };

  const handleDeleteClick = (coupon) => {
    setCouponToDelete(coupon);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    if (!couponToDelete) return;
    
    try {
      // Simulation de suppression dans Firestore
      // Dans une implémentation réelle, cela serait remplacé par un appel à l'API
      await deleteDoc(doc(firestore, 'coupons', couponToDelete.id));
      
      // Mettre à jour l'état local
      setCoupons(coupons.filter(coupon => coupon.id !== couponToDelete.id));
      
      setSnackbar({
        open: true,
        message: 'Coupon supprimé avec succès',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting coupon:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression du coupon',
        severity: 'error'
      });
    } finally {
      setOpenDeleteDialog(false);
      setCouponToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
    setCouponToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const filteredCoupons = coupons.filter(coupon => {
    // Filtre de recherche
    const matchesSearch = 
      coupon.code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      coupon.id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filtre de statut
    const matchesStatus = filters.status === 'all' || coupon.status === filters.status;
    
    // Filtre de type
    const matchesType = filters.type === 'all' || coupon.discountType === filters.type;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'primary';
      case 'expired':
        return 'default';
      case 'disabled':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'expired':
        return 'Expiré';
      case 'disabled':
        return 'Désactivé';
      default:
        return status;
    }
  };

  const getDiscountTypeLabel = (type) => {
    switch (type) {
      case 'percentage':
        return 'Pourcentage';
      case 'fixed':
        return 'Montant fixe';
      default:
        return type;
    }
  };

  const formatDate = (dateString) => {
    const options = { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const formatDiscount = (type, value) => {
    if (type === 'percentage') {
      return `${value}%`;
    } else if (type === 'fixed') {
      return `${value}€`;
    }
    return value;
  };

  return (
    <Box className={classes.root}>
      <Paper className={classes.paper}>
        <div className={classes.headerActions}>
          <Typography className={classes.title} variant="h4" component="h1">
            Coupons
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateCoupon}
          >
            Nouveau coupon
          </Button>
        </div>
        
        <div className={classes.filters}>
          <TextField
            className={classes.searchField}
            label="Rechercher"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel id="status-filter-label">Statut</InputLabel>
            <Select
              labelId="status-filter-label"
              id="status-filter"
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              label="Statut"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="active">Actif</MenuItem>
              <MenuItem value="expired">Expiré</MenuItem>
              <MenuItem value="disabled">Désactivé</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel id="type-filter-label">Type de réduction</InputLabel>
            <Select
              labelId="type-filter-label"
              id="type-filter"
              name="type"
              value={filters.type}
              onChange={handleFilterChange}
              label="Type de réduction"
            >
              <MenuItem value="all">Tous</MenuItem>
              <MenuItem value="percentage">Pourcentage</MenuItem>
              <MenuItem value="fixed">Montant fixe</MenuItem>
            </Select>
          </FormControl>
        </div>

        {loading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table className={classes.table} aria-label="coupons table">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Réduction</TableCell>
                  <TableCell>Valeur min.</TableCell>
                  <TableCell>Validité</TableCell>
                  <TableCell>Utilisations</TableCell>
                  <TableCell>Statut</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCoupons.length > 0 ? (
                  filteredCoupons.map((coupon) => (
                    <TableRow key={coupon.id}>
                      <TableCell component="th" scope="row">
                        <strong>{coupon.code}</strong>
                      </TableCell>
                      <TableCell>{coupon.description}</TableCell>
                      <TableCell>
                        {formatDiscount(coupon.discountType, coupon.discountValue)}
                        {coupon.maxDiscountAmount > 0 && coupon.discountType === 'percentage' && (
                          <Typography variant="caption" display="block">
                            (max. {coupon.maxDiscountAmount}€)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {coupon.minOrderValue > 0 ? `${coupon.minOrderValue}€` : '-'}
                      </TableCell>
                      <TableCell>
                        {formatDate(coupon.startDate)} - {formatDate(coupon.endDate)}
                      </TableCell>
                      <TableCell>
                        {coupon.usageCount}
                        {coupon.usageLimit > 0 && (
                          <Typography variant="caption" display="block">
                            (limite: {coupon.usageLimit})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={getStatusLabel(coupon.status)} 
                          color={getStatusColor(coupon.status)} 
                          size="small"
                          className={classes.chip}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton 
                          aria-label="view" 
                          color="primary"
                          onClick={() => handleViewCoupon(coupon.id)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton 
                          aria-label="edit" 
                          color="primary"
                          onClick={() => handleEditCoupon(coupon.id)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton 
                          aria-label="delete" 
                          color="secondary"
                          onClick={() => handleDeleteClick(coupon)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Aucun coupon trouvé
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Dialog de confirmation de suppression */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Confirmer la suppression"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Êtes-vous sûr de vouloir supprimer définitivement le coupon <strong>{couponToDelete?.code}</strong> ? Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Annuler
          </Button>
          <Button onClick={handleDeleteConfirm} color="secondary" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar pour les notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CouponsListPage;
